import {useEffect,useLayoutEffect,useMemo,useRef} from 'react';
import type {Card,Snapshot} from '@platform/contracts/x';
import {cardWonStages,prioritizeWinningCards} from './cardPrizes';

// Proximity only becomes meaningful after a confirmed ball in an active game.
// Preparing is still a valid owned-card view, but is not a live ranking.
export function hasLiveCardProgress(state:Snapshot|null){
 return !!state&&state.status==='Live'&&state.tombala.status==='Live'&&state.balls.length>0&&!['completed','cancelled'].includes(state.tombala.nextStageId);
}

// Ownership is independent of the public near-winner strip. Only membership,
// stage, remaining counts and confirmed wins invalidate it, not pool updates.
export function useRankedOwnedCards(state:Snapshot){
 const owned=useMemo(()=>state.cards.filter(c=>c.ownMinor>0),[state.cards]);
 const previous=useRef<{drawId:string;ids:string[]}>({drawId:'',ids:[]});
 const active=hasLiveCardProgress(state);
 // Final results put every prize-winning card first, including earlier cinkos.
 // Final distances and the last committed order break ties within each group.
 const settled=(state.status==='Completed'||state.tombala.status==='Completed')&&state.status!=='Cancelled'&&state.tombala.status!=='Cancelled';
 const ranked=active||settled;
 const scoreKey=JSON.stringify(owned.map(c=>[c.id,ranked?c.numbersToNextStage:null,settled?cardWonStages(c,state.awards):null]));
 const ids=useMemo(()=>{
  const order=new Map((previous.current.drawId===state.drawId?previous.current.ids:[]).map((id,i)=>[id,i]));
  const sorted=[...owned].sort((a,b)=>(ranked?a.numbersToNextStage-b.numbersToNextStage:0)||
   (order.get(a.id)??Number.MAX_SAFE_INTEGER)-(order.get(b.id)??Number.MAX_SAFE_INTEGER));
  return (settled?prioritizeWinningCards(sorted,state.awards):sorted).map(c=>c.id);
  // scoreKey includes confirmed wins, so late awards also update the result.
 },[state.drawId,state.tombala.nextStageId,ranked,settled,scoreKey]);
 useLayoutEffect(()=>{previous.current={drawId:state.drawId,ids};},[state.drawId,ids]);
 return useMemo(()=>{const byId=new Map(owned.map(c=>[c.id,c]));return ids.map(id=>byId.get(id)!);},[owned,ids]);
}

// One projection for the strip and spectator cards, using already received
// data. Equal distances preserve the previous committed order across updates.
export function useNearestCards(state:Snapshot|null){
 const previous=useRef<{drawId:string;ids:string[]}>({drawId:'',ids:[]});
 const active=hasLiveCardProgress(state);
 const settled=!!state&&state.status==='Completed'&&state.tombala.status!=='Cancelled'&&state.balls.length>0;
 const cards=useMemo(()=>{
  if((!active&&!settled)||!state)return [];
  const order=new Map((previous.current.drawId===state.drawId?previous.current.ids:[]).map((id,i)=>[id,i]));
  // Keep the same spectator cards through the result hold, with their latest
  // cells/last ball. A direct final load can still rank the authoritative result.
  if(settled&&order.size)return [...state.cards].sort((a,b)=>(order.get(a.id)??Infinity)-(order.get(b.id)??Infinity)||a.id.localeCompare(b.id));
  return [...state.cards]
   .sort((a,b)=>a.numbersToNextStage-b.numbersToNextStage||(order.get(a.id)??Number.MAX_SAFE_INTEGER)-(order.get(b.id)??Number.MAX_SAFE_INTEGER)||a.id.localeCompare(b.id));
 },[active,settled,state?.drawId,state?.cards]);
 useEffect(()=>{previous.current={drawId:state?.drawId??'',ids:cards.map(c=>c.id)};},[state?.drawId,cards]);
 return cards;
}
